/**
 * Toggle switches in MUI, Android, square, and default styles.
 */
import { FormControlLabel, styled, Switch } from "@mui/material";
import React from "react";
import PropTypes from "prop-types";

import { asBool, asFunc } from "utils/safeData";

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff"
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: theme.palette.mode === "dark" ? "#003892" : "#001e3c",
    width: 32,
    height: 32,
    "&:before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        "#fff"
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
    borderRadius: 20 / 2,
  },
}));

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&:before, &:after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&:before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&:after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(theme.palette.getContrastText(theme.palette.primary.main))}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

const SquareSwitch = styled(Switch)(() => ({
  padding: 0,
  height: 30,
  width: 32,
  borderRadius: 2,
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 500 500"><path fill="${encodeURIComponent("#9CA0A0")}" d="M306.3 32.62 65.46 252.86 312 478.8l-29.84 32.62L0 252.83 276.46 0z"/></svg>')`,
      },
    },
  },
  "& .MuiSwitch-track": {
    borderRadius: 2,
    backgroundColor: "#9B9F9F !important",
    opacity: "1 !important",
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    borderRadius: 2,
    width: 14,
    height: 26,
    transform: "translate(-7px, -7px)",
    "&:before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 2,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 500 500"><path fill="${encodeURIComponent("#9CA0A0")}" d="M35.54 0 312 252.82 29.84 511.42 0 478.8l246.54-225.94L5.7 32.62z"/></svg>')`,
    },
  },
}));

/**
 * Labeled switch that toggles a boolean via `setChecked`.
 *
 * @param {{ checked?: boolean, setChecked?: Function, children?: React.ReactNode, color?: string, type?: "MUI"|"Android"|"square"|"normal", className?: string }} props
 */
const VSwitch = (props) => {
  const { children, color, type, className, ...rest } = props;
  const checked = asBool(props.checked);
  const setChecked = asFunc(props.setChecked);

  /**
   * Flips the checked state.
   *
   * @returns {void}
   */
  const handleCheck = () => {
    // e.preventDefault();
    // e.stopPropagation();
    setChecked(!checked);
  };

  return (
    <>
      {type === "MUI" && <FormControlLabel control={<MaterialUISwitch checked={checked} onChange={handleCheck} name={children} color={color} />} className={className} label={children} {...rest} />}
      {type === "Android" && <FormControlLabel control={<Android12Switch checked={checked} onChange={handleCheck} name={children} color={color} />} className={className} label={children} {...rest} />}
      {type === "square" && <FormControlLabel control={<SquareSwitch checked={checked} onChange={handleCheck} name={children} color={color} />} className={className} label={children} sx={{ borderRadius: "2px" }} {...rest} />}
      {type === "normal" && (
        <FormControlLabel
          sx={{
            display: "block",
          }}
          control={<Switch checked={checked} onChange={handleCheck} name={children} color={color} />}
          className={className}
          label={children}
          {...rest}
        />
      )}
    </>
  );
};

VSwitch.defaultProps = {
  checked: false,
  setChecked: () => {},
  children: "",
  color: "primary",
  type: "normal",
};

VSwitch.propTypes = {
  checked: PropTypes.bool,
  setChecked: PropTypes.func,
  children: PropTypes.node,
  color: PropTypes.string,
  type: PropTypes.oneOf(["MUI", "Android", "square", "normal"]),
  className: PropTypes.string,
};

export default VSwitch;
